import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({ items: [] });
  const [sessionId, setSessionId] = useState('');

  // Get or create session ID
  useEffect(() => {
    let sid = localStorage.getItem('cart_session_id');
    if (!sid) {
      sid = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('cart_session_id', sid);
    }
    setSessionId(sid);
    loadCart(sid);
  }, []);

  const loadCart = async (sid) => {
    try {
      const response = await axios.get(`${API}/cart/${sid}`);
      setCart(response.data);
    } catch (error) {
      console.error('Error loading cart:', error);
      setCart({ items: [] });
    }
  };

  const addToCart = async (productId, productType, quantity = 1) => {
    try {
      const response = await axios.post(`${API}/cart?session_id=${sessionId}`, {
        product_id: productId,
        product_type: productType,
        quantity: quantity
      });
      setCart(response.data);
      return true;
    } catch (error) {
      console.error('Error adding to cart:', error);
      return false;
    }
  };

  const updateQuantity = async (productId, quantity) => {
    try {
      const response = await axios.put(
        `${API}/cart/${sessionId}/items/${productId}`,
        { quantity: quantity }
      );
      setCart(response.data);
    } catch (error) {
      console.error('Error updating quantity:', error);
    }
  };

  const removeFromCart = async (productId) => {
    try {
      await axios.delete(`${API}/cart/${sessionId}/items/${productId}`);
      await loadCart(sessionId);
    } catch (error) {
      console.error('Error removing from cart:', error);
    }
  };

  const clearCart = async () => {
    try {
      await axios.delete(`${API}/cart/${sessionId}`);
      setCart({ items: [] });
    } catch (error) {
      console.error('Error clearing cart:', error);
    }
  };

  const getCartTotal = () => {
    return cart.items?.reduce((total, item) => total + (item.price * item.quantity), 0) || 0;
  };

  const getCartCount = () => {
    return cart.items?.reduce((count, item) => count + item.quantity, 0) || 0;
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        sessionId,
        addToCart,
        updateQuantity,
        removeFromCart,
        clearCart,
        getCartTotal,
        getCartCount,
        refreshCart: () => loadCart(sessionId)
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
